import { datatype, internet, lorem, random } from 'faker'

import { emptyCharacter } from '../../components/EmptyChar'
import { getLongRatings, getShortRatings, LongRatings, ShortRatings } from '../../models'
import { Tenant } from '../Tenants/models'

type Currency = string

export interface IssuerTier {
  id: number
  issuerId: number
  instrumentCode: string
  tier: number
}

export interface Issuer {
  id: number
  name: string
  shortName: string
  code: string
  createdAt: Date
  updatedAt: Date
  frontOfficeEmail: string
  backOfficeEmail: string
  leiCode?: string
  clearCode?: string
  bankingGroupId: number
  fundsFossilFuels: boolean
  longRating: LongRatings
  shortRating: ShortRatings
  domain: string
  bankingGroupName: string
  currencies?: Currency[]
  kycCustomerCode?: string
  tradesCount?: number
  rfqsCount?: number
}

export const mockIssuer = (overrides?: Partial<Issuer>): Issuer => ({
  id: datatype.number(),
  name: lorem.word(),
  shortName: lorem.words(3),
  code: lorem.word(),
  createdAt: datatype.datetime(),
  updatedAt: datatype.datetime(),
  frontOfficeEmail: internet.email(),
  backOfficeEmail: internet.email(),
  leiCode: lorem.word(),
  clearCode: lorem.word(),
  bankingGroupId: datatype.number(),
  fundsFossilFuels: datatype.boolean(),
  shortRating: random.arrayElement(getShortRatings()),
  longRating: random.arrayElement(getLongRatings()),
  domain: lorem.word(),
  bankingGroupName: lorem.word(),
  currencies: [],
  kycCustomerCode: lorem.word(),
  tradesCount: datatype.number(),
  rfqsCount: datatype.number(),
  ...overrides,
})

export interface IssuerFormValues {
  id?: number
  name: string
  shortName: string
  code: string
  frontOfficeEmail: string[]
  backOfficeEmail: string[]
  bankingGroupId: number
  leiCode: string
  clearCode: string
  fundsFossilFuels: boolean
  belongsToBankingGroup?: boolean
  shortRating: ShortRatings
  longRating: LongRatings
}

export const issuerFormInitialValues: IssuerFormValues = {
  id: null,
  name: '',
  shortName: '',
  code: '',
  frontOfficeEmail: [],
  backOfficeEmail: [],
  bankingGroupId: null,
  leiCode: '',
  clearCode: '',
  fundsFossilFuels: false,
  belongsToBankingGroup: false,
  shortRating: null,
  longRating: null,
}

export interface RfqEmail {
  id?: number
  issuerId: number
  tenantId?: number
  instrumentCode: string
  name: string
  email: string
}

export interface RfqEmailFormValues {
  issuerId: number
  tenantId?: number
  instrumentCode: string
  name: string
  email: string[]
}

export const rfqEmailFormInitialValues: RfqEmailFormValues = {
  issuerId: null,
  tenantId: null,
  instrumentCode: null,
  name: '',
  email: [],
}

export const transformIssuersToTableItems = (issuers: Issuer[], issuerTiers: IssuerTier[]) =>
  issuers?.map((issuer) => ({
    ...issuer,
    bankingGroup: `${issuer.bankingGroupName} - ${issuer.bankingGroupId}`,
    tiers: issuerTiers?.filter((tier) => tier.issuerId === issuer.id) || [],
  })) || []

export const transformRfqEmailsToTableItems = (rfqEmails: RfqEmail[], issuers: Issuer[], tenants: Tenant[]) =>
  issuers && tenants && rfqEmails
    ? rfqEmails?.map((rfqEmail) => ({
        ...rfqEmail,
        issuerName: getIssuerName(issuers, rfqEmail.issuerId),
        tenantName: getTenantName(tenants, rfqEmail.tenantId),
        instrumentCode: rfqEmail.instrumentCode || emptyCharacter,
      }))
    : []

const getIssuerName = (issuers: Issuer[], issuerId: number) => {
  if (!issuerId) {
    return emptyCharacter
  }

  const issuer = issuers.find((issuer) => issuer.id === issuerId)

  return issuer ? issuer.shortName : emptyCharacter
}

const getTenantName = (tenants: Tenant[], tenantId: number) => {
  if (!tenantId) {
    return emptyCharacter
  }

  const tenant = tenants.find((tenant) => tenant.id === tenantId)

  return tenant ? tenant.name : emptyCharacter
}
